import { computed } from 'vue';

import { useYardInTransitFeature } from '@satellite/composables/yard/useYardInTransitFeature';

import type { YardAssetSelectAxes } from '@satellite/composables/yard/yardAssetSelectRules';
import type { ComputedRef } from 'vue';

/**
 * What the picker showed before OD-4127: parked assets only, and never one already being moved.
 *
 * Every axis is spelled out rather than left to its fallback — the missing and in-transit ones
 * default to inert, which is right for a picker that never knew about them and wrong here.
 */
const LEGACY_MOVE_TASK_ASSET_SELECT_AXES: YardAssetSelectAxes = {
  includeAssignedAssets: true,
  includeWaitingAssignmentAssets: false,
  includeMissingAssets: false,
  includeInTransitAssets: false,
  includeAssociatedToActiveMoveTasks: 'disabled'
};

/**
 * Only a parked asset can start a move. Every other state is still listed — an operator looking for
 * a trailer should find it and see why it cannot be picked, rather than watch it vanish.
 *
 * `includeAssociatedToActiveMoveTasks` is deliberately back to `true`: holding an open move task is
 * not itself a reason to refuse. The asset is refused once that move is under way, and In Transit
 * is what says so.
 *
 * Assets hooked to a carrier visit never reach here — `GET yard/view/assets` filters them out at
 * the source — so "not listed" holds even under Circe's "Show disabled" toggle.
 */
const MOVE_TASK_ASSET_SELECT_AXES: YardAssetSelectAxes = {
  includeAssignedAssets: true,
  includeWaitingAssignmentAssets: 'disabled',
  includeMissingAssets: 'disabled',
  includeInTransitAssets: 'disabled',
  includeAssociatedToActiveMoveTasks: true
};

/**
 * The one rule set behind "What asset needs to be moved?", in Helios and in Circe, on create and on
 * update. Gated on `enable-yard-in-transit`; with the flag off both pickers behave as they did.
 */
export function useYardMoveTaskAssetSelectAxes(): ComputedRef<YardAssetSelectAxes> {
  const { isYardInTransitVisible } = useYardInTransitFeature();

  return computed(() =>
    isYardInTransitVisible.value ? MOVE_TASK_ASSET_SELECT_AXES : LEGACY_MOVE_TASK_ASSET_SELECT_AXES
  );
}
